import SingleChoicePreview from '../../QuestionPreviews/SingleChoicePreview/SingleChoicePreview';
import DraggableList from '../../../../../../../components/Draggable/DraggableList/DraggableList';
import generateNumberList from '../../../../helpers/rangeListGenerator';
import { Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  IconButton,
  FormControl,
  Grid,
  FormHelperText,
  CircularProgress
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Droppable } from 'react-beautiful-dnd';
import { useSnackbar } from 'notistack';
import {
  addQuestionOption,
  removeQuestionOption,
  updateQuestion,
  updateQuestionOption
} from '../../../../../../../store/actions/dataEntryTemplateGeneratorActions';
import { uploadFile, presigneUrl } from '../../../../../../../services/upload/upload.service';
import globalConfig from '../../../../../../../config/globalConfig';
import './SingleChoiceQuestion.css';

const { isDevelopment, storage } = globalConfig;
const bucketFolder = 'dataentry/templates/files';
const origin = isDevelopment ? storage : window.location.origin;

const SingleChoiceQuestion = ({ question, onChangeAlert, templateType }) => {
  const { config, variant, options: questionOptions, isActive, id, error } = question;
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [uploadingImages, setUploadingImages] = useState({});
  const [isMounted, setIsMounted] = useState(false);
  const [draggableOptions, setDraggableOptions] = useState([]);
  const tenant = useSelector((state) => state?.tenant?.tenant ?? {});
  const isSurveyTemplate = templateType?.value === 'survey';

  // Use a simple droppable ID like in the working example
  const droppableId = useMemo(() => `QUESTION_DROPPABLE_${id}`, [id]);

  // On React 18 we need to load Droppable element after the component is mounted
  // See: https://github.com/atlassian/react-beautiful-dnd/issues/2399
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    event.preventDefault();
    event.stopPropagation();

    // Allow empty input
    if (value === '') {
      const newRange = { ...config.range, [name]: '' };
      dispatch(
        updateQuestion({
          id,
          config: { range: newRange },
          options: []
        })
      );
      onChangeAlert();
      return;
    }

    // Only allow numbers with optional 2 decimal places, decimal separator must be "."
    const decimalRegex = /^\d+(\.\d{0,2})?$/;

    if (decimalRegex.test(value)) {
      const newValue = parseFloat(value);
      const newRange = { ...config.range, [name]: value };

      const min = Number(newRange?.min || 0);
      const max = Number(newRange?.max || 0);
      const skip = Number(newRange?.skip || 0);

      dispatch(
        updateQuestion({
          id,
          config: { range: newRange },
          options: [...generateNumberList(min, max, skip)]
        })
      );
    }

    onChangeAlert();
  };

  const handleAddOption = () => {
    const payload = {
      questionId: question.id,
      option: {
        id: new Date().getTime(),
        value: '',
        option: '',
        order: questionOptions.length + 1,
        image: null,
        error: {}
      }
    };

    dispatch(addQuestionOption(payload));
    onChangeAlert();
  };

  const handleRemoveOption = (option) => {
    dispatch(removeQuestionOption({ questionId: question.id, optionId: option?.id }));
    onChangeAlert();
  };

  const handleChangeOption = (event, optionId) => {
    const payload = {
      questionId: question.id,
      optionId,
      option: { [event.target.name]: event.target.value }
    };

    dispatch(updateQuestionOption(payload));
    onChangeAlert();
  };

  const handleImageUpload = async (event, optionId) => {
    const file = event.target.files[0];

    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/avif',
      'image/svg+xml'
    ];
    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar('Please select a valid image file (JPEG, PNG, GIF, WebP)', {
        variant: 'error'
      });
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      enqueueSnackbar('Image size must be less than 5MB', {
        variant: 'error'
      });
      return;
    }

    setUploadingImages((prev) => ({ ...prev, [optionId]: true }));

    try {
      const files = [
        {
          file: { path: file?.path ?? '' },
          lastModified: file?.lastModified ?? new Date().getTime(),
          name: file?.name ?? '',
          size: file?.size ?? 0,
          type: file?.type || 'image/png'
        }
      ];

      const resultUpload = await presigneUrl(files, bucketFolder);

      if (resultUpload.error || !resultUpload.data) {
        return { error: resultUpload.error.message };
      } else {
        const dataImage =
          resultUpload?.data && resultUpload?.data?.length ? resultUpload?.data[0]?.data : null;

        if (dataImage) {
          await uploadFile(dataImage.presignedUrl, file);

          const imageUrl = `${origin}/tenants/${tenant?.tenantId}/${bucketFolder}/${dataImage.filename}`;

          const payload = {
            questionId: question.id,
            optionId,
            option: { image: imageUrl, error: null }
          };

          dispatch(updateQuestionOption(payload));
          onChangeAlert();
        }
      }
    } catch (error) {
      enqueueSnackbar('Failed to upload image. Please try again.', {
        variant: 'error'
      });
    } finally {
      setUploadingImages((prev) => ({ ...prev, [optionId]: false }));
      // Clear the input value to allow re-uploading the same file
      event.target.value = '';
    }
  };

  const handleRemoveImage = (optionId) => {
    const payload = {
      questionId: question.id,
      optionId,
      option: { image: null }
    };

    dispatch(updateQuestionOption(payload));
    onChangeAlert();
  };

  // Setup draggable items
  useEffect(() => {
    const setupItems = () => {
      if (questionOptions && questionOptions?.length > 0 && variant?.value !== 'range') {
        const safeOptions = [];
        questionOptions.forEach((option, index) => {
          const newOption = {
            originalOption: option,
            id: option?.id || index,
            itemId: option?.id,
            order: option?.order || index + 1,
            content: (
              <Grid className='single-choice-options__grid' container alignItems='center'>
                {/* Drag Handle */}
                <Grid
                  item
                  xs={0.5}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'grab',
                    '&:active': {
                      cursor: 'grabbing'
                    }
                  }}
                >
                  <FontAwesomeIcon
                    icon='fa-solid fa-grip-dots-vertical'
                    style={{ marginRight: '1rem', color: '#9CA3AF' }}
                  />
                </Grid>

                {/* Value Container with Icon */}
                <Grid item xs={3} className='single-choice-options__value-container'>
                  <FontAwesomeIcon
                    icon={['far', 'circle']}
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '10px',
                      flexShrink: 0
                    }}
                  />
                  {variant && variant?.value === 'boolean' ? (
                    <FormControl style={{ minWidth: 150, width: '100%' }}>
                      <Select
                        label='Value'
                        name='value'
                        value={option.value}
                        error={!!(option?.error?.value && option?.error?.value !== '')}
                        onChange={(event) => handleChangeOption(event, option?.id)}
                        className='custom-text-field'
                        variant='standard'
                      >
                        <MenuItem value={true}>true</MenuItem>
                        <MenuItem value={false}>false</MenuItem>
                      </Select>
                      {option?.error?.value && option?.error?.value !== '' && (
                        <FormHelperText className='custom-text-helper--error'>
                          {option?.error?.value}
                        </FormHelperText>
                      )}
                    </FormControl>
                  ) : variant && variant?.value === 'numeric' ? (
                    <TextField
                      label='Value'
                      type='number'
                      name='value'
                      value={option?.value || ''}
                      error={!!(option?.error?.value && option?.error?.value !== '')}
                      helperText={option?.error?.value}
                      onChange={(event) => handleChangeOption(event, option?.id)}
                      style={{ mr: 1, width: '100%' }}
                      className='custom-text-field'
                      variant='standard'
                    />
                  ) : (
                    <TextField
                      label='Value'
                      name='value'
                      value={option?.value || ''}
                      error={!!(option?.error?.value && option?.error?.value !== '')}
                      helperText={option?.error?.value}
                      onChange={(event) => handleChangeOption(event, option?.id)}
                      style={{ mr: 1, width: '100%' }}
                      className='custom-text-field'
                      variant='standard'
                    />
                  )}
                </Grid>

                {/* Option Field */}
                <Grid item xs={7.5} style={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    label='Option'
                    name='option'
                    value={option?.option || ''}
                    error={!!(option?.error?.option && option?.error?.option !== '')}
                    helperText={option?.error?.option}
                    onChange={(event) => handleChangeOption(event, option?.id)}
                    style={{ mr: 1, flex: 1, width: '100%' }}
                    className='custom-text-field'
                    variant='standard'
                  />
                  {isSurveyTemplate && variant?.value === 'image' && (
                    <Box className='single-choice-options__image-container'>
                      {option?.image ? (
                        <Box className='single-choice-options__image-preview'>
                          <img
                            src={option.image}
                            alt={option?.option || 'Option image'}
                            className='single-choice-options__image-upload'
                            draggable={false}
                          />
                          <Box className='single-choice-options__image-actions'>
                            <input
                              type='file'
                              accept='image/*'
                              id={`image-upload-${option.id}`}
                              style={{ display: 'none' }}
                              onChange={(event) => handleImageUpload(event, option?.id)}
                            />
                            <IconButton
                              size='small'
                              onClick={() => document.getElementById(`image-upload-${option?.id}`).click()}
                              disabled={uploadingImages[option.id]}
                              title='Change image'
                            >
                              <FontAwesomeIcon icon={['fal', 'edit']} />
                            </IconButton>
                            <IconButton
                              size='small'
                              onClick={() => handleRemoveImage(option.id)}
                              disabled={uploadingImages[option.id]}
                              title='Remove image'
                            >
                              <FontAwesomeIcon icon={['fal', 'trash']} />
                            </IconButton>
                          </Box>
                        </Box>
                      ) : (
                        <Box>
                          <Box
                            className={`single-choice-options__image-upload-container ${
                              option?.error?.image ? 'single-choice-options__image--error ' : ''
                            }`}
                          >
                            <input
                              type='file'
                              accept='image/*'
                              id={`image-upload-${option.id}`}
                              style={{ display: 'none' }}
                              onChange={(event) => handleImageUpload(event, option?.id)}
                            />
                            <Tooltip title={uploadingImages[option.id] ? 'Uploading...' : 'Upload Image'}>
                              <span>
                                <IconButton
                                  size='small'
                                  onClick={() =>
                                    document.getElementById(`image-upload-${option?.id}`).click()
                                  }
                                  disabled={uploadingImages[option?.id]}
                                >
                                  {uploadingImages[option?.id] ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <FontAwesomeIcon icon={['fal', 'upload']} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                          {option?.error?.image && option?.error?.image !== '' && (
                            <FormHelperText className='custom-text-helper--error'>
                              {option?.error?.image}
                            </FormHelperText>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={1} className='single-choice-options__action'>
                  {questionOptions.length > 1 &&
                    (questionOptions.length > config?.minOptions ||
                      config?.minOptions === -1 ||
                      !config?.minOptions) && (
                      <IconButton
                        onClick={() => handleRemoveOption(option)}
                        className='single-choice-options__action-button'
                        size='small'
                      >
                        <FontAwesomeIcon
                          icon='circle-minus'
                          className='single-choice-options__action-button__icon'
                        />
                      </IconButton>
                    )}
                  {(config?.maxOptions === -1 || questionOptions.length < config?.maxOptions) && (
                    <IconButton
                      onClick={handleAddOption}
                      className='single-choice-options__action-button'
                      size='small'
                    >
                      <FontAwesomeIcon icon='plus' className='single-choice-options__action-button__icon' />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            )
          };
          safeOptions.push(newOption);
        });
        setDraggableOptions(safeOptions);
      } else {
        setDraggableOptions([]);
      }
    };

    setupItems();
  }, [questionOptions, variant, config, isSurveyTemplate, uploadingImages]);

  // Don't render drag and drop if not active
  if (!isActive) {
    return <SingleChoicePreview question={question} templateType={templateType} />;
  }

  // Render range variant (no drag and drop for range)
  if (variant && variant?.value === 'range') {
    return (
      <Box className='single-choice-options'>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            gap: '12px',
            alignContent: 'center'
          }}
        >
          <Box>
            <TextField
              label='From'
              name='min'
              value={`${config?.range?.min}`}
              error={!!(error?.min && error?.min !== '')}
              helperText={error?.min}
              onChange={handleChange}
              style={{ mr: 1, flex: 1, width: '160px' }}
              className='custom-text-field'
              variant='standard'
            />
          </Box>

          <Box>
            <TextField
              label='To'
              name='max'
              value={`${config?.range?.max}`}
              error={!!(error?.max && error?.max !== '')}
              helperText={error?.max}
              onChange={handleChange}
              style={{ mr: 1, flex: 1, width: '160px' }}
              className='custom-text-field'
              variant='standard'
            />
          </Box>

          <Box>
            <TextField
              label='Skip'
              name='skip'
              value={`${config?.range?.skip}`}
              error={!!(error?.skip && error?.skip !== '')}
              helperText={error?.skip}
              onChange={handleChange}
              style={{ mr: 1, flex: 1, width: '160px' }}
              className='custom-text-field'
              variant='standard'
              inputProps={{
                inputMode: 'decimal',
                pattern: '\\d+(\\.\\d{0,2})?'
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className='single-choice-options'>
      {!isMounted ? (
        // Static version for initial render
        <Box>
          {questionOptions.map((option, index) => (
            <Grid
              key={option?.id || `static-${index}`}
              className='single-choice-options__grid'
              container
              alignItems='center'
            >
              <Grid item xs={0.5}>
                <FontAwesomeIcon
                  icon='fa-solid fa-grip-dots-vertical'
                  style={{ marginRight: '1rem', color: '#9CA3AF' }}
                />
              </Grid>
              <Grid item xs={3} className='single-choice-options__value-container'>
                <FontAwesomeIcon
                  icon={['far', 'circle']}
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '10px',
                    flexShrink: 0
                  }}
                />
                {variant && variant?.value === 'boolean' ? (
                  <FormControl style={{ minWidth: 150, width: '100%' }}>
                    <Select
                      label='Value'
                      name='value'
                      value={option.value}
                      error={!!(option?.error?.value && option?.error?.value !== '')}
                      onChange={(event) => handleChangeOption(event, option?.id)}
                      className='custom-text-field'
                      variant='standard'
                    >
                      <MenuItem value={true}>true</MenuItem>
                      <MenuItem value={false}>false</MenuItem>
                    </Select>
                    {option?.error?.value && option?.error?.value !== '' && (
                      <FormHelperText className='custom-text-helper--error'>
                        {option?.error?.value}
                      </FormHelperText>
                    )}
                  </FormControl>
                ) : variant && variant?.value === 'numeric' ? (
                  <TextField
                    label='Value'
                    type='number'
                    name='value'
                    value={option?.value || ''}
                    error={!!(option?.error?.value && option?.error?.value !== '')}
                    helperText={option?.error?.value}
                    onChange={(event) => handleChangeOption(event, option?.id)}
                    style={{ mr: 1, width: '100%' }}
                    className='custom-text-field'
                    variant='standard'
                  />
                ) : (
                  <TextField
                    label='Value'
                    name='value'
                    value={option?.value || ''}
                    error={!!(option?.error?.value && option?.error?.value !== '')}
                    helperText={option?.error?.value}
                    onChange={(event) => handleChangeOption(event, option?.id)}
                    style={{ mr: 1, width: '100%' }}
                    className='custom-text-field'
                    variant='standard'
                  />
                )}
              </Grid>
              <Grid item xs={7.5} style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  label='Option'
                  name='option'
                  value={option?.option || ''}
                  error={!!(option?.error?.option && option?.error?.option !== '')}
                  helperText={option?.error?.option}
                  onChange={(event) => handleChangeOption(event, option?.id)}
                  style={{ mr: 1, flex: 1, width: '100%' }}
                  className='custom-text-field'
                  variant='standard'
                />
                {isSurveyTemplate && variant?.value === 'image' && (
                  <Box className='single-choice-options__image-container'>
                    {option?.image ? (
                      <Box className='single-choice-options__image-preview'>
                        <img
                          src={option.image}
                          alt={option?.option || 'Option image'}
                          className='single-choice-options__image-upload'
                          draggable={false}
                        />
                        <Box className='single-choice-options__image-actions'>
                          <input
                            type='file'
                            accept='image/*'
                            id={`image-upload-${option.id}`}
                            style={{ display: 'none' }}
                            onChange={(event) => handleImageUpload(event, option?.id)}
                          />
                          <IconButton
                            size='small'
                            onClick={() => document.getElementById(`image-upload-${option?.id}`).click()}
                            disabled={uploadingImages[option.id]}
                            title='Change image'
                          >
                            <FontAwesomeIcon icon={['fal', 'edit']} />
                          </IconButton>
                          <IconButton
                            size='small'
                            onClick={() => handleRemoveImage(option.id)}
                            disabled={uploadingImages[option.id]}
                            title='Remove image'
                          >
                            <FontAwesomeIcon icon={['fal', 'trash']} />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Box
                          className={`single-choice-options__image-upload-container ${
                            option?.error?.image ? 'single-choice-options__image--error ' : ''
                          }`}
                        >
                          <input
                            type='file'
                            accept='image/*'
                            id={`image-upload-${option.id}`}
                            style={{ display: 'none' }}
                            onChange={(event) => handleImageUpload(event, option?.id)}
                          />
                          <Tooltip title={uploadingImages[option.id] ? 'Uploading...' : 'Upload Image'}>
                            <span>
                              <IconButton
                                size='small'
                                onClick={() => document.getElementById(`image-upload-${option?.id}`).click()}
                                disabled={uploadingImages[option?.id]}
                              >
                                {uploadingImages[option?.id] ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <FontAwesomeIcon icon={['fal', 'upload']} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                        {option?.error?.image && option?.error?.image !== '' && (
                          <FormHelperText className='custom-text-helper--error'>
                            {option?.error?.image}
                          </FormHelperText>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Grid>
              <Grid item xs={1} className='single-choice-options__action'>
                {questionOptions.length > 1 &&
                  (questionOptions.length > config?.minOptions ||
                    config?.minOptions === -1 ||
                    !config?.minOptions) && (
                    <IconButton
                      onClick={() => handleRemoveOption(option)}
                      className='single-choice-options__action-button'
                      size='small'
                    >
                      <FontAwesomeIcon
                        icon='circle-minus'
                        className='single-choice-options__action-button__icon'
                      />
                    </IconButton>
                  )}
                {(config?.maxOptions === -1 || questionOptions.length < config?.maxOptions) && (
                  <IconButton
                    onClick={handleAddOption}
                    className='single-choice-options__action-button'
                    size='small'
                  >
                    <FontAwesomeIcon icon='plus' className='single-choice-options__action-button__icon' />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}
        </Box>
      ) : (
        // Draggable version after mount
        <Droppable droppableId={droppableId}>
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`single-choice-options__container ${
                snapshot.isDraggingOver ? 'single-choice-options__container--dragging-over' : ''
              }`}
            >
              <DraggableList items={draggableOptions} isDragDisabled={false} style={{ margin: '8px 0' }} />
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </Box>
  );
};

export default SingleChoiceQuestion;
